import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import RowOptions from "../../Widgets/RowOptions";
import SectionWrapper from "../../Widgets/SectionWrapper";
import DataTable, { DataTableColumn, DataTableRow } from "../../Widgets/DataTable";
import ActivityModal from "./ActivityModal";
import useGetSocialHistory from "hooks/api/client/useGetSocialHistory";
import useCreateSocialHistory from "hooks/api/client/useCreateSocialHistory";
import useUpdateSocialHistory from "hooks/api/client/useUpdateSocialHistory";
import useDeleteSocialHistory from "hooks/api/client/useDeleteSocialHistory";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";

const BASE_COLUMNS: DataTableColumn[] = [
  { key: "activity", label: "Activity" },
  { key: "startDate", label: "Start date" },
  { key: "endDate", label: "End date" },
  { key: "levelOfParticipation", label: "Level of Participation" },
];

const EDIT_COLUMN: DataTableColumn = { key: "actions", label: "" };

const DEGREE_LABELS: Record<string, string> = {
  INDEPENDENT: "Independent",
  PARTIALLY_SUPPORTED: "Partially supported",
  FULLY_SUPPORTED: "Fully supported",
};

const parseDateOnly = (value: string | null | undefined): Date | null =>
  value ? dateWithoutTimezone(value) : null;

// Sent as a fixed UTC midnight so the API's DateTime column accepts it; parseDateOnly
// only reads the literal date part back, so this doesn't reintroduce the timezone shift.
const toDateOnlyString = (date: Date | null): string | null =>
  date ? `${format(date, "yyyy-MM-dd")}T00:00:00.000Z` : null;

const formatDate = (value: string | null | undefined): string => {
  const date = parseDateOnly(value);
  if (!date) return "-";
  return date.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
};

export const SocialActivities: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { socialHistory, refetchSocialHistory } = useGetSocialHistory(clientIdNum);

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  const { createSocialHistoryMutation, isCreateSocialHistoryLoading } = useCreateSocialHistory({
    onSuccess: () => {
      refetchSocialHistory();
      closeModal();
    },
  });

  const { updateSocialHistoryMutation, isUpdateSocialHistoryLoading } = useUpdateSocialHistory({
    onSuccess: () => {
      refetchSocialHistory();
      closeModal();
    },
  });

  const { deleteSocialHistoryMutation } = useDeleteSocialHistory({
    onSuccess: () => {
      refetchSocialHistory();
    },
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DataTableRow | null>(null);
  const canEdit = true; // Going to replace it with actual permission check later

  const columns = canEdit ? [...BASE_COLUMNS, EDIT_COLUMN] : BASE_COLUMNS;

  const rows: DataTableRow[] = socialHistory.map((item: any) => ({
    id: String(item.id),
    activity: item.activityType || "-",
    startDate: formatDate(item.startDate),
    endDate: formatDate(item.endDate),
    levelOfParticipation: DEGREE_LABELS[item.degreeOfIndependence] || item.degreeOfIndependence || "-",
    _degreeOfIndependence: item.degreeOfIndependence || "",
    _startDateIso: item.startDate || null,
    _endDateIso: item.endDate || null,
  }));

  const handleSave = (formValues: {
    activityName: string;
    startDate: Date | null;
    endDate: Date | null;
    levelOfParticipation: string;
  }) => {
    if (!clientIdNum) return;
    const payload = {
      activityType: formValues.activityName || null,
      degreeOfIndependence: formValues.levelOfParticipation || null,
      startDate: toDateOnlyString(formValues.startDate),
      endDate: toDateOnlyString(formValues.endDate),
    };

    if (selectedRow) {
      updateSocialHistoryMutation({ id: Number(selectedRow.id), payload });
    } else {
      createSocialHistoryMutation({ clientId: clientIdNum, payload });
    }
  };

  return (
    <SectionWrapper
      title="Social History"
      description="Past and current activities that help identify environments where the client thrives or may need additional support."
      hideButtons
    >
      <DataTable
        columns={columns}
        rows={rows}
        ariaLabel="Social history"
        emptyMessage="No social history records"
        onAddNew={() => { setSelectedRow(null); setModalOpen(true); }}
        renderCell={(col, row) => {
          if (col.key === "actions") {
            return (
              <RowOptions
                onEdit={() => { setSelectedRow(row); setModalOpen(true); }}
                onDelete={() => deleteSocialHistoryMutation(Number(row.id))}
              />
            );
          }
          return row[col.key] || "-";
        }}
      />

      <ActivityModal
        open={modalOpen}
        onClose={closeModal}
        onSave={handleSave}
        isSaving={isCreateSocialHistoryLoading || isUpdateSocialHistoryLoading}
        initialValues={selectedRow ? {
          activityName: selectedRow.activity !== "-" ? (selectedRow.activity as string) : "",
          startDate: parseDateOnly(selectedRow._startDateIso as string | null),
          endDate: parseDateOnly(selectedRow._endDateIso as string | null),
          levelOfParticipation: selectedRow._degreeOfIndependence as string,
        } : undefined}
      />
    </SectionWrapper>
  );
};

export default SocialActivities;
